import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store/store";

/** CSS selector for the scrollable parent container */
const SCROLL_CONTAINER_SELECTOR = ".single-order-layout--content";

/** CSS selector for the tab navigation element that becomes sticky */
const TAB_NAVIGATION_SELECTOR =
  ".single-order-layout--content--secondary__left--navigation";

/**
 * Checks if any loading state is active
 * @param isLoading - Main order loading state
 * @param isWorkflowConfigLoading - Workflow config loading state
 * @param isFormConfigLoading - Form config loading state
 * @returns True if any loading is in progress
 */
const isAnyLoadingActive = (
  isLoading: boolean,
  isWorkflowConfigLoading: boolean,
  isFormConfigLoading: boolean,
): boolean => {
  return isLoading || isWorkflowConfigLoading || isFormConfigLoading;
};

/**
 * Calculates the initial scroll threshold at which the tab should become sticky.
 * This is the distance from the scroll container's top to the tab element's top.
 *
 * @param scrollContainer - The scrollable container element
 * @param tabElement - The tab navigation element
 * @returns The scroll threshold in pixels, or null if elements are not available
 */
const calculateStickyThreshold = (
  scrollContainer: Element | null,
  tabElement: Element | null,
): number | null => {
  if (!scrollContainer || !tabElement) return null;

  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = tabElement.getBoundingClientRect();

  return elementRect.top - containerRect.top;
};

/**
 * Determines if the sticky state should be active based on scroll position
 *
 * @param scrollContainer - The scrollable container element
 * @param threshold - The scroll threshold at which sticky activates
 * @returns True if scroll position is past the threshold
 */
const shouldBeSticky = (
  scrollContainer: Element,
  threshold: number,
): boolean => {
  const scrollTop = (scrollContainer as HTMLElement).scrollTop;
  return scrollTop >= threshold;
};

/**
 * Hook that manages the sticky state of the tab navigation element.
 * Monitors scroll position and determines when the tab bar should become fixed.
 *
 * @returns Object containing:
 *   - shouldMakeSticky: Boolean indicating if the tab should be sticky
 *   - setShouldMakeSticky: Setter function to manually control sticky state
 *
 * @example
 * ```tsx
 * const { shouldMakeSticky } = useMakeTabSticky();
 *
 * return (
 *   <div style={{ position: shouldMakeSticky ? 'fixed' : 'relative' }}>
 *     <TabNavigation />
 *   </div>
 * );
 * ```
 */
export const useMakeTabSticky = () => {
  const [shouldMakeSticky, setShouldMakeSticky] = useState<boolean>(false);

  const { isLoading } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { isLoading: isWorkflowConfigLoading } = useSelector(
    (state: RootState) => state.workflowConfig,
  );
  const { loading: isFormConfigLoading } = useSelector(
    (state: RootState) => state.formConfigNoLiveListen,
  );

  /** Stores the scroll threshold at which sticky should activate */
  const stickyThresholdRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      isAnyLoadingActive(
        isLoading ?? false,
        isWorkflowConfigLoading ?? false,
        isFormConfigLoading ?? false,
      )
    ) {
      return;
    }

    let rafId: number | null = null;
    let scrollContainer: HTMLElement | null = null;

    const updateStickyState = () => {
      if (scrollContainer && stickyThresholdRef.current !== null) {
        setShouldMakeSticky(
          shouldBeSticky(scrollContainer, stickyThresholdRef.current),
        );
      }
    };

    const tryInit = () => {
      scrollContainer = document.querySelector(
        SCROLL_CONTAINER_SELECTOR,
      ) as HTMLElement | null;
      const tabElement = document.querySelector(TAB_NAVIGATION_SELECTOR);

      if (!scrollContainer || !tabElement) {
        rafId = requestAnimationFrame(tryInit);
        return;
      }

      stickyThresholdRef.current = calculateStickyThreshold(
        scrollContainer,
        tabElement,
      );

      scrollContainer.addEventListener("scroll", updateStickyState);
      window.addEventListener("resize", updateStickyState);
      updateStickyState();
    };

    tryInit();

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      scrollContainer?.removeEventListener("scroll", updateStickyState);
      window.removeEventListener("resize", updateStickyState);
      stickyThresholdRef.current = null;
    };
  }, [isLoading, isWorkflowConfigLoading, isFormConfigLoading]);

  return { shouldMakeSticky, setShouldMakeSticky };
};
